import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { TwTheme } from '../../../Tw';
import {
  formatInteger,
  FormattedValueWithUnit,
  getCurrencySymbolSettings,
} from '../../common/formatHelper';
import { getDefaultMeasure, getMeasureTitle } from '../../common/measureHelper';
import useFormatter from '../../common/useFormatter';
import ButtonSwitchMulti from '../../components/actions/ButtonSwitchMulti';
import GenericMetricChart, {
  ChartDataItem,
} from '../../components/charts/GenericMetricChart';
import StatItem from '../../components/stats/StatItem';
import PanelContainer from '../../containers/PanelContainer';
import { getProcessTypeColor } from '../../dashboard/kpi/utilityFunctions';
import { warehouseSelected } from '../../store/warehouse.state';
import useActualityAggregationMetricDescriptors from '../hooks/useActualityAggregationMetricDescriptors';
import { actualityProcessStats } from '../store/actuality.kpi.state';
import {
  ActualityAggregatedHqExtendedDataRow,
  ActualityAggregatedHqExtendedStatsDataColumn,
} from '../store/actuality.kpi.types';
import ActualityAggregateMetricSelector from './ActualityAggregateMetricSelector';

const colors = TwTheme.extend.colors;

export type FeedEventProcessTypeSummaryProps = {
  className?: string;
};

const FeedEventProcessTypeSummary: React.FC<
  FeedEventProcessTypeSummaryProps
> = ({ className }) => {
  const { t } = useTranslation('feed');
  const { t: tMeasures } = useTranslation('measures');
  const stats = useRecoilValue(actualityProcessStats);
  const wh = useRecoilValue(warehouseSelected);
  const formatter = useFormatter();
  const aggMetricDescriptors = useActualityAggregationMetricDescriptors();
  const [currentMetric, setCurrentMetric] = useState<
    'share' | 'events' | 'duration' | 'cost' | 'distance' | 'advanced'
  >('share');
  const [selectedMetric, setSelectedMetric] =
    useState<ActualityAggregatedHqExtendedStatsDataColumn>('eventsCount');
  const [showDistribution, setShowDistribution] = useState(false);

  const systemCurrency = getCurrencySymbolSettings(wh.currency).symbol;
  const sizeMeasureValue = getMeasureTitle(
    getDefaultMeasure(wh.measurementSystem, 'distance'),
    tMeasures,
  );

  const totalEventsCountRaw = _.sumBy(stats, 'eventsCount') || 0;

  const getMetricValue = (row: ActualityAggregatedHqExtendedDataRow) => {
    switch (currentMetric) {
      case 'share':
        return totalEventsCountRaw > 0
          ? (row.eventsCount / totalEventsCountRaw) * 100
          : 0;
      case 'events':
        return row.eventsCount || 0;
      case 'duration':
        return row.analyzedDuration || 0;
      case 'cost':
        return row.analyzedCost || 0;
      case 'distance':
        return (
          (row.analyzedDistance || 0) +
          (row.analyzedDistanceTravellingStart || 0) +
          (row.analyzedDistanceTravellingEnd || 0) +
          (row.analyzedDistanceTravellingInternal || 0) +
          (row.analyzedDistanceTravellingVertical || 0)
        );
      case 'advanced':
        return _.get(row, selectedMetric) || 0;
      default:
        return row.eventsCount || 0;
    }
  };

  const getTotalValue = () => {
    switch (currentMetric) {
      case 'share':
        return 100; // 100% for share
      case 'events':
        return totalEventsCountRaw;
      case 'duration':
        return _.sumBy(stats, 'analyzedDuration') || 0;
      case 'cost':
        return _.sumBy(stats, 'analyzedCost') || 0;
      case 'distance':
        return (
          _.sumBy(
            stats,
            row =>
              (row.analyzedDistance || 0) +
              (row.analyzedDistanceTravellingStart || 0) +
              (row.analyzedDistanceTravellingEnd || 0) +
              (row.analyzedDistanceTravellingInternal || 0) +
              (row.analyzedDistanceTravellingVertical || 0),
          ) || 0
        );
      case 'advanced':
        return _.sumBy(stats, row => _.get(row, selectedMetric) || 0) || 0;
      default:
        return totalEventsCountRaw;
    }
  };

  const getFormattedValue = (value: number): FormattedValueWithUnit => {
    switch (currentMetric) {
      case 'share':
        return formatter.formatShare(value / 100);
      case 'events':
        return {
          value: formatInteger(value),
          fullString: `${formatInteger(value)}`,
          unit: t`events`,
          raw: value,
          unitCode: 'events',
        };
      case 'duration':
        return aggMetricDescriptors['analyzedDuration'].getFormatted(value);
      case 'cost':
        return aggMetricDescriptors['analyzedCost'].getFormatted(value);
      case 'distance':
        return aggMetricDescriptors['analyzedDistance'].getFormatted(value);
      case 'advanced':
        const metricDescriptor = aggMetricDescriptors[selectedMetric];
        if (metricDescriptor && _.isNumber(value)) {
          const formatted = metricDescriptor.getFormatted(value);
          return formatted;
        }
        return {
          value: value.toString(),
          fullString: value.toString(),
          unit: '',
          raw: value,
          unitCode: 'metric',
        };
      default:
        return {
          value: formatInteger(value),
          fullString: `${formatInteger(value)}`,
          unit: t`events`,
          raw: value,
          unitCode: 'events',
        };
    }
  };

  const getTotalLabel = () => {
    switch (currentMetric) {
      case 'share':
        return t`Total Share`;
      case 'events':
        return t`Total Events`;
      case 'duration':
        return t`Total Duration`;
      case 'cost':
        return t`Total Cost`;
      case 'distance':
        return t`Total Distance`;
      case 'advanced':
        return aggMetricDescriptors[selectedMetric]?.title || t`Total`;
      default:
        return t`Total`;
    }
  };

  // Get the current metric descriptor for visualization recommendations
  const getCurrentMetricDescriptor = () => {
    if (currentMetric === 'advanced') {
      return aggMetricDescriptors[selectedMetric];
    }
    // For built-in metrics, we can define basic descriptors or use defaults
    return null;
  };

  const data: ChartDataItem[] = _.chain(stats)
    .map(row => {
      const eventProcessType = row.eventProcessType || 'OTHER';
      const value = getMetricValue(row);
      // Get human-readable name or fall back to process type
      const displayName =
        eventProcessType === 'OTHER'
          ? t`Other`
          : eventProcessType || t`Unknown Process`;

      return {
        id: eventProcessType,
        value,
        label: displayName,
        color: getProcessTypeColor(eventProcessType),
        formatted: getFormattedValue(value),
      };
    })
    .filter(item => item.value > 0)
    .sortBy(item => -item.value)
    .value();

  const totalValue = getTotalValue();
  const formattedTotal = getFormattedValue(totalValue);

  const metricSwitch = (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <ButtonSwitchMulti
          autoSize
          buttonType="minimal"
          options={[
            {
              label: t`Share`,
              icon: '%',
            },
            {
              label: t`Events`,
              icon: 'events',
            },
            {
              label: t`Duration`,
              icon: 'h',
            },
            {
              label: t`Cost`,
              icon: systemCurrency,
            },
            {
              label: t`Distance`,
              icon: sizeMeasureValue,
            },
            {
              label: t`Advanced`,
              icon: '...',
            },
          ]}
          selectedIndex={[
            'share',
            'events',
            'duration',
            'cost',
            'distance',
            'advanced',
          ].indexOf(currentMetric)}
          onClick={index => {
            setCurrentMetric(
              ['share', 'events', 'duration', 'cost', 'distance', 'advanced'][
                index
              ] as typeof currentMetric,
            );
          }}
        />

        {/* Distribution Toggle Button */}
        {/* <Button
          className={cn(
            'rounded border !p-1',
            showDistribution ? 'bg-menu/30 border-menu/20' : 'border-menu/50',
          )}
          buttonIcon={
            <Icon.DataStatsPie
              className={cn(
                'fill-current',
                showDistribution ? 'text-menu-text' : 'text-menu-text',
                'group-hover:opacity-50',
                'h-4 w-4',
              )}
            />
          }
          buttonSize="xs"
          isTransparent
          hasIconAfter
          onPress={() => setShowDistribution(!showDistribution)}
        /> */}
      </div>

      {currentMetric === 'advanced' && (
        <div className="w-full">
          <ActualityAggregateMetricSelector
            value={selectedMetric}
            onChange={setSelectedMetric}
          />
        </div>
      )}
    </div>
  );

  if (!data.length) {
    return null;
  }

  return (
    <PanelContainer
      id="panel-feed-event-process-type-summary"
      title={t`Process Summary`}
      collapsible
      hasPadding
      className={className}
    >
      {metricSwitch}

      <GenericMetricChart
        data={data}
        metricDescriptor={getCurrentMetricDescriptor()}
        showChartTypeSelector={false}
        containerHeight={300}
        showCenterPie
        defaultChartType="pie"
        totalValue={totalValue}
        totalFormattedValue={formattedTotal.value}
        centerPieProps={{
          statLabelMiddle: getTotalLabel(),
          statValueMiddle: formattedTotal.value,
        }}
      />

      {/* {showDistribution ? (
        // Show the new generic chart component with multiple chart type options
        <GenericMetricChart
          data={data}
          metricDescriptor={getCurrentMetricDescriptor()}
          showChartTypeSelector={true}
          containerHeight={300}
          showCenterPie
          totalValue={totalValue}
          totalFormattedValue={formattedTotal.value}
          centerPieProps={{
            statLabelMiddle: getTotalLabel(),
            statValueMiddle: formattedTotal.value,
          }}
        />
      ) : (
        // Show the traditional center pie chart with stats
        <ContainerChart containerMaxHeight={200}>
          <StatCenterPie
            statLabelMiddle={getTotalLabel()}
            statValueMiddle={formattedTotal.value}
          />
          <SimulationPieChart data={data} />
        </ContainerChart>
      )} */}

      {/* Stats List */}
      {_(data)
        .map(item => (
          <StatItem
            key={`process-type-${item.id}`}
            title={item.label}
            value={item.formatted}
            legendColor={item.color[0]}
          />
        ))
        .value()}
    </PanelContainer>
  );
};

export default FeedEventProcessTypeSummary;
